/**
 * Main App Router Component
 * Handles routing and authentication for StepFlow AI
 *
 * Route loading:
 * - Heavy authenticated pages are code-split via React.lazy so the initial
 *   bundle only contains the shell, landing, and login pages. Other routes
 *   are downloaded on demand.
 * - Each lazy route is wrapped in a RouteErrorBoundary so a crash in one
 *   page doesn't blank the whole app.
 * - Shared <Suspense/> fallback keeps the layout stable while chunks load.
 *
 * Routes:
 * - / → Public marketing page (LandingPage)
 * - /login → Login page
 * - /callback → OAuth redirect (Cognito Hosted UI / Google)
 * - /dashboard → Authenticated dashboard (portfolio view)
 * - /agentic → Agentic Networks (role USER; invited members)
 * - /network/:id → Network workbench (CREATOR+; USER redirected to /agentic)
 * - /usage → Workspace usage (tenant-scoped); ?tab=org for org rollup (OWNER/ADMIN + orgId)
 * - /workspace/settings, /workspace/members → OWNER + ADMIN only (CREATOR redirected)
 * - /org/usage → Redirects to /usage?tab=org
 * - /pricing → Plans & Stripe subscription checkout
 * - /join?token= → Accept workspace link invite (public; sign-in if needed)
 * - /network/new → Create new network flow (redirects to workbench)
 * - /dashboard, /app → Legacy; redirect by role (/network/new vs /agentic)
 */
import React, { lazy, Suspense } from 'react';
import DialogHost from '@stepflow/ui-shared/components/DialogHost.jsx';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import ProtectedRoute from './ProtectedRoute.jsx';
import OnboardingGate from './OnboardingGate.jsx';
import SubscriptionGate from '@stepflow/ui-billing/SubscriptionGate.jsx';
import RouteErrorBoundary from './RouteErrorBoundary.jsx';
import RequiresNetworkWorkbenchAccess from './RequiresNetworkWorkbenchAccess.jsx';
import RequiresWorkspaceAdminAccess from './RequiresWorkspaceAdminAccess.jsx';
import { useAuth } from './context/AuthContext';
import { canAccessNetworkWorkbench } from '@/utils/stepflowRoles';
import { APP_BROWSE_DEFAULT_HOME } from '@stepflow/ui-shared/constants/appBrowseFlags.js';
import { homeFor } from '@stepflow/ui-shared/utils/lastPlace.js';
import './App.css';

// Lazy-loaded pages + shell. Keeps the initial bundle to auth/layout glue; each
// area loads as its own chunk with RouteErrorBoundary + Suspense (see LazyRoute).
const LandingPage = lazy(() => import('@stepflow/ui-auth/LandingPage.jsx'));
const LoginPage = lazy(() => import('@stepflow/ui-auth/LoginPage.jsx'));
const OAuthCallbackPage = lazy(() => import('@stepflow/ui-auth/OAuthCallbackPage.jsx'));
const WorkbenchShell = lazy(() => import('./WorkbenchShell.jsx'));
const NetworkWorkbench = lazy(() => import('@stepflow/ui-workbench/NetworkWorkbench.jsx'));
const AgenticNetworkPage = lazy(() => import('@stepflow/ui-agentic/AgenticNetworkPage.jsx'));
const AppBrowsePage = lazy(() => import('@stepflow/ui-workbench/AppBrowsePage.jsx'));
const InboxPage = lazy(() => import('./InboxPage.jsx'));
const AccountUsagePage = lazy(() => import('@stepflow/ui-billing/AccountUsagePage.jsx'));
const WorkspaceSettingsPage = lazy(() => import('@stepflow/ui-admin/WorkspaceSettingsPage.jsx'));
const WorkspaceMembersPage = lazy(() => import('@stepflow/ui-admin/WorkspaceMembersPage.jsx'));
const AdminPoolDiscoveryPage = lazy(() => import('@stepflow/ui-admin/AdminPoolDiscoveryPage.jsx'));
const AdminApprovalsOverwatchPage = lazy(() => import('@stepflow/ui-admin/AdminApprovalsOverwatchPage.jsx'));
const PricingPage = lazy(() => import('@stepflow/ui-billing/PricingPage.jsx'));
const JoinWorkspacePage = lazy(() => import('@stepflow/ui-auth/JoinWorkspacePage.jsx'));

function RouteFallback() {
  return (
    <div className="flex min-h-[40vh] items-center justify-center text-sm text-gray-500">
      Loading…
    </div>
  );
}

/**
 * Wraps a lazy page with error boundary + suspense so each route is isolated.
 * Keying the boundary by pathname auto-clears a crash state when the user
 * navigates away, so the error UI can't get stuck across routes.
 */
function LazyRoute({ name, children, persistent = false }) {
  const location = useLocation();
  // Per-route content resets its error boundary on navigation (``key={pathname}``) so a caught
  // error can't get stuck across routes. But a PERSISTENT LAYOUT (the WorkbenchShell that owns the
  // left nav) must NOT be keyed by pathname — that would unmount+remount the whole shell (and its
  // sidebar: networks refetch/flash, collapse state resets) on EVERY navigation. Layout children
  // keep their own per-path LazyRoute, so content errors still reset; the layout stays mounted.
  return (
    <RouteErrorBoundary key={persistent ? name : location.pathname} routeName={name}>
      <Suspense fallback={<RouteFallback />}>{children}</Suspense>
    </RouteErrorBoundary>
  );
}

function PostAuthHomeRedirect() {
  const { workspaceRole, activeTenantId } = useAuth();
  // Network-workbench roles land on the app-browse home (/apps) when the flag is
  // on (reversible), else the classic create flow (/network/new). USER role is
  // unaffected — it always goes to /agentic. FIRST: where the person left off in
  // this workspace (LOGIN-TO-TENANT §8a — validated by the server).
  const workbenchHome = APP_BROWSE_DEFAULT_HOME ? '/registry' : '/network/new';
  const can = canAccessNetworkWorkbench(workspaceRole);
  return (
    <Navigate
      to={homeFor(activeTenantId, { canWorkbench: can, fallback: can ? workbenchHome : '/agentic' })}
      replace
    />
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <ToastProvider>
        {/* the app's ONE dialog (confirm / alert / prompt) — browser dialogs are banned house-wide */}
        <DialogHost />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LazyRoute name="LandingPage"><LandingPage /></LazyRoute>} />
          <Route path="/login" element={<LazyRoute name="LoginPage"><LoginPage /></LazyRoute>} />
          <Route path="/callback" element={<LazyRoute name="OAuthCallbackPage"><OAuthCallbackPage /></LazyRoute>} />
          <Route
            path="/join"
            element={<LazyRoute name="JoinWorkspacePage"><JoinWorkspacePage /></LazyRoute>}
          />

          {/* Protected Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><PostAuthHomeRedirect /></ProtectedRoute>} />

          <Route
            element={
              <ProtectedRoute>
                {/* no workspace yet → Join / Create / invite-only, never the shell (LOGIN-TO-TENANT.md §3) */}
                <OnboardingGate>
                  <SubscriptionGate>
                    <LazyRoute name="WorkbenchShell" persistent>
                      <WorkbenchShell />
                    </LazyRoute>
                  </SubscriptionGate>
                </OnboardingGate>
              </ProtectedRoute>
            }
          >
            <Route
              path="/agentic"
              element={<LazyRoute name="AgenticNetworkPage"><AgenticNetworkPage /></LazyRoute>}
            />
            {/* one place for what needs you (INBOX.md) */}
            <Route
              path="/inbox"
              element={<LazyRoute name="InboxPage"><InboxPage /></LazyRoute>}
            />
            <Route
              path="/registry"
              element={<LazyRoute name="AppBrowsePage"><AppBrowsePage /></LazyRoute>}
            />
            <Route
              path="/usage"
              element={
                <RequiresNetworkWorkbenchAccess>
                  <LazyRoute name="AccountUsagePage"><AccountUsagePage /></LazyRoute>
                </RequiresNetworkWorkbenchAccess>
              }
            />
            <Route
              path="/pricing"
              element={<LazyRoute name="PricingPage"><PricingPage /></LazyRoute>}
            />
            <Route path="/org/usage" element={<Navigate to="/usage?tab=org" replace />} />
            <Route
              path="/workspace/settings"
              element={
                <RequiresNetworkWorkbenchAccess>
                  <RequiresWorkspaceAdminAccess>
                    <LazyRoute name="WorkspaceSettingsPage"><WorkspaceSettingsPage /></LazyRoute>
                  </RequiresWorkspaceAdminAccess>
                </RequiresNetworkWorkbenchAccess>
              }
            />
            <Route
              path="/workspace/members"
              element={
                <RequiresNetworkWorkbenchAccess>
                  <RequiresWorkspaceAdminAccess>
                    <LazyRoute name="WorkspaceMembersPage"><WorkspaceMembersPage /></LazyRoute>
                  </RequiresWorkspaceAdminAccess>
                </RequiresNetworkWorkbenchAccess>
              }
            />
            <Route
              path="/admin/pools"
              element={
                <RequiresWorkspaceAdminAccess>
                  <LazyRoute name="AdminPoolDiscoveryPage"><AdminPoolDiscoveryPage /></LazyRoute>
                </RequiresWorkspaceAdminAccess>
              }
            />
            <Route
              path="/admin/approvals"
              element={
                <RequiresWorkspaceAdminAccess>
                  <LazyRoute name="AdminApprovalsOverwatchPage"><AdminApprovalsOverwatchPage /></LazyRoute>
                </RequiresWorkspaceAdminAccess>
              }
            />
            <Route
              path="/network/:id"
              element={
                <RequiresNetworkWorkbenchAccess>
                  <LazyRoute name="NetworkWorkbench"><NetworkWorkbench /></LazyRoute>
                </RequiresNetworkWorkbenchAccess>
              }
            />
          </Route>

          {/* Legacy routes - redirect to workbench */}
          <Route path="/app" element={<ProtectedRoute><PostAuthHomeRedirect /></ProtectedRoute>} />

          {/* Fallback: Redirect unknown routes */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ToastProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
